import { ArrowLeft1Light, ArrowRight1Light, CloseLight } from '@ha/icons';
import React from 'react';

import { Grid, IconButton } from '@hbf/dsl';
import { useMediaQuery, useOrientation } from 'ha/helpers/MediaQuery';

import { makeStyles } from 'tss-react/mui';
import { SlideIndex } from './SlideIndex';
import { GalleryThumbnails as Thumbnails } from './Thumbnails';
import { Navigation } from './Navigation';

import { TabsContent } from '../../Tabs';

const useStyles = makeStyles()(theme => ({
  background: {
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    zIndex: -1,
    backgroundColor: 'black',
  },
  root: {
    height: '100%',
    zIndex: theme.zIndex.modal,
    backgroundColor: 'black',
  },
  overlayButton: {
    strokeWidth: 2,
    stroke: theme.palette.grey[200],
    filter: 'drop-shadow( 0px 2px 2px rgba(0, 0, 0, .7))',
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  previousButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    left: theme.spacing(1),
  },
  nextButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: theme.spacing(1),
  },
  gallery: {
    position: 'relative',
    margin: theme.spacing(1, 1, 0),
    [`${theme.breakpoints.up('md')}, (orientation: landscape)`]: {
      margin: theme.spacing(1, 1, 1, 0),
    },
  },
  navigation: {
    padding: theme.spacing(0.5),
    width: 'auto',
  },
  thumbnails: {},
  slideIndex: {
    backgroundColor: theme.palette.action.active,
    borderRadius: theme.spacing(0.5),
    position: 'absolute',
    top: theme.spacing(1),
    left: '50%',
    transform: 'translateX(-50%)',
  },
}));

interface Props {
  contentIndex: number;
  // Set of slides being rendered
  slides: React.ReactNode | null;
  // Set of thumbnails being rendered
  thumbnails: string[];
  // The current content should render controls
  withControls: boolean;
  // Whether to show thumbnails
  withThumbnails?: boolean;
  // Whether to show slide index
  withSlideIndex?: boolean;
  // The user can set the total number of slides given content
  total: number;
  // The current index of slide being shown
  slideIndex: number;
  // Allows the user to set an index slide. Example: if you render a thumbnails component
  // you can click in one thumbnail and set the right index
  handleChangeSlideIndex: (index: number) => void;
  // Handle next slide
  handleNext: () => void;
  // Handle previous slide
  handlePrevious: () => void;
  // Handle how the content is changed
  handleChangeContentIndex: (i: number) => void;
  // Set of navigation tabs
  navigationContent: TabsContent[];
  // How to close the modal
  onClose: () => void;
}

const Gallery: React.FC<Props> = ({
  onClose,
  contentIndex,
  slides,
  total,
  handlePrevious,
  handleNext,
  slideIndex,
  handleChangeSlideIndex,
  thumbnails,
  handleChangeContentIndex,
  navigationContent,
  withControls,
  withThumbnails,
  withSlideIndex,
}) => {
  const { classes } = useStyles();
  const { md } = useMediaQuery();
  const orientation = useOrientation();

  return (
    <React.Fragment>
      {/* following background div is workaround for page content being displayed in bottom for mobile browsers (Chrome at least, device-only) when transitioning toolbar visibility  */}
      <div className={classes.background} />

      <Grid
        container
        direction="column"
        wrap="nowrap"
        className={classes.root}
        data-test-locator="Gallery"
      >
        <Grid
          item
          xs
          container
          direction={
            md || orientation === 'landscape' ? 'row-reverse' : 'column'
          }
          justifyContent="center"
        >
          <Grid item xs className={classes.gallery}>
            {slides}
            {withControls && total > 1 && (
              <React.Fragment>
                <IconButton
                  className={`${classes.overlayButton} ${classes.previousButton}`}
                  onClick={handlePrevious}
                  data-test-locator="gallery-control-left"
                  size="large"
                >
                  <ArrowLeft1Light />
                </IconButton>
                <IconButton
                  className={`${classes.overlayButton} ${classes.nextButton}`}
                  onClick={handleNext}
                  data-test-locator="gallery-control-right"
                  size="large"
                >
                  <ArrowRight1Light />
                </IconButton>
              </React.Fragment>
            )}
            {withSlideIndex && (
              <div className={classes.slideIndex}>
                <SlideIndex total={total} index={slideIndex} />
              </div>
            )}
          </Grid>
          <Grid
            item
            xs="auto"
            container
            alignContent={md ? 'flex-start' : 'center'}
            justifyContent="center"
            className={classes.navigation}
          >
            <Navigation
              contentIndex={contentIndex}
              handleChangeContentIndex={handleChangeContentIndex}
              handleChangeSlideIndex={handleChangeSlideIndex}
              navigationContent={navigationContent}
              orientation={
                md || orientation === 'landscape' ? 'vertical' : 'horizontal'
              }
              withText={md}
            />
          </Grid>
        </Grid>

        {withThumbnails && md && (
          <Grid item xs="auto" container justifyContent="center">
            <Thumbnails
              data-test-locator="gallery-thumbnails"
              handleChangeIndex={handleChangeSlideIndex}
              thumbnails={thumbnails}
              index={slideIndex}
            />
          </Grid>
        )}

        <IconButton
          onClick={onClose}
          className={`${classes.overlayButton} ${classes.closeButton}`}
          data-test-locator="Modal Close Button"
          size="large"
        >
          <CloseLight fontSize="small" />
        </IconButton>
      </Grid>
    </React.Fragment>
  );
};

export { Gallery };
